import type { LocaleCode } from '@/i18n'

export type LegalDocumentType = 'privacyPolicy' | 'termsOfUse'

export interface LegalDocument {
  title: string
  rawText: string
}

export const legalDocuments: Record<LegalDocumentType, Record<LocaleCode, LegalDocument>> = {
  privacyPolicy: {
    en: {
      title: 'Privacy Policy',
      rawText: `Privacy Policy
Achiraya Studio  |  www.jackachihouse.com
Effective Date: 1 June 2025
1. Introduction
Achiraya Studio ('the Studio', 'we', 'us', 'our') provides professional makeup services, authentic Thai costume rental and dressing, and photography at Wat Arun Ratchawararam (Temple of Dawn), Bangkok, Thailand. We are committed to protecting the personal data of our customers and website visitors.
This Privacy Policy explains what personal data we collect, how we use it, to whom we may disclose it, and the rights you have in relation to it. We process personal data in accordance with the Personal Data Protection Act B.E. 2562 (2019) (PDPA) of the Kingdom of Thailand.
2. Data We Collect
2.1 Data You Provide Directly
• Full name and contact details (email address, phone number)
• Booking information: preferred date, time, and type of service selected
• Payment information — processed via Omise Payment Gateway (we do not store your card data directly)
• Special requests or additional notes you submit with your booking
2.2 Data Collected Automatically
• Website usage data (IP address, browser type, pages visited, session duration)
• Cookies and similar tracking technologies
2.3 Photography Data
• Photographs taken during your session are delivered to you and may be used in Studio communications and marketing materials with your prior consent
3. Purpose of Use
We use your personal data for the following purposes:
• Processing bookings and confirming appointments
• Processing payments securely via the Omise payment platform
• Sending booking confirmations, reminders, and service updates by email or SMS
• Improving service quality and customer experience
• Sending promotional offers and newsletters (only with your explicit consent)
• Complying with applicable laws and regulations
4. Payment Processing — Omise
The Studio uses Omise (Omise Co., Ltd.), a PCI-DSS certified payment gateway, to handle all online payment transactions.
• Credit and debit card data is collected and stored directly by Omise — Achiraya Studio does not store card details on its own servers
• Omise's handling of your payment data is governed by the Omise Privacy Policy, available at www.omise.co/privacy
• The Studio retains only: transaction reference number, amount charged, transaction date, and payment status
5. Disclosure to Third Parties
We do not sell, rent, or share your personal data with third parties except in the following circumstances:
• Service providers: Omise (payments), hosting providers, and email delivery services — bound by contractual data protection obligations
• Government or regulatory authorities: when required by law, court order, or legal process
• Emergency situations: to protect the safety or rights of you or another person
6. Data Retention
• Booking and payment records: 5 years from the date of transaction (in accordance with Thai tax and accounting requirements)
• Marketing data: until you withdraw consent or unsubscribe
• User account data: for the life of the account, or 2 years after the last activity
• Website logs and analytics data: no longer than 1 year
7. Your Rights under the PDPA
Under the Personal Data Protection Act B.E. 2562, you have the following rights:
• Right of Access — request a copy of the personal data we hold about you
• Right of Rectification — request correction of inaccurate or incomplete data
• Right of Erasure — request deletion of your data when it is no longer necessary
• Right to Object — object to processing of your data for direct marketing purposes
• Right to Withdraw Consent — withdraw consent at any time without affecting prior processing
• Right to Data Portability — receive your data in a structured, commonly used format

To exercise any of these rights, please contact us using the details in Section 10. We will respond within 30 days of receiving your request.
8. Security Measures
• All data transmitted to and from our website is encrypted using TLS/SSL
• Payment card data is protected under Omise's PCI-DSS Level 1 certification
• Access to personal data is restricted on a need-to-know basis (Principle of Least Privilege)
• Regular data backups are performed and stored securely
9. Cookies
The website jackachihouse.com uses cookies for the following purposes:
• Essential Cookies: required for the booking system and core site functionality
• Analytics Cookies: to understand how visitors interact with the site (e.g. Google Analytics)
• Marketing Cookies: to serve relevant advertisements (only with your consent)

You can manage your cookie preferences via the consent banner displayed on the website, or by adjusting your browser settings.
10. Contact Us
For questions about this Privacy Policy, or to exercise your data rights, please contact:

Achiraya Studio
• Website: www.jackachihouse.com
• Email: support@jackachihouse.com
• Phone: (+66) 83 013 1218  or  (+66) 92 498 8850
• Address: 36 Arun Amarin Road, Wat Kalaya Subdistrict, Thonburi District, Bangkok 10600, Thailand
11. Policy Updates
We may update this Privacy Policy from time to time. Any material changes will be communicated via the website or by email. The effective date at the top of this document will always reflect the most recent version.


─── End of Privacy Policy ───`,
    },
    th: {
      title: 'นโยบายความเป็นส่วนตัว',
      rawText: `นโยบายความเป็นส่วนตัว
Achiraya Studio  |  www.jackachihouse.com
มีผลบังคับใช้: 1 มิถุนายน 2568 (Effective: 1 June 2025)
1. บทนำ (Introduction)
Achiraya Studio ('สตูดิโอ', 'เรา') ให้บริการแต่งหน้ามืออาชีพ เช่าและสวมชุดไทยแท้ และถ่ายภาพที่วัดอรุณราชวราราม กรุงเทพมหานคร เราให้ความสำคัญกับการคุ้มครองข้อมูลส่วนบุคคลของลูกค้าและผู้ใช้บริการทุกท่าน
นโยบายฉบับนี้อธิบายถึงประเภทของข้อมูลที่เราเก็บรวบรวม วิธีการใช้ข้อมูล การเปิดเผยข้อมูล และสิทธิ์ของท่าน เราดำเนินการตามพระราชบัญญัติคุ้มครองข้อมูลส่วนบุคคล พ.ศ. 2562 (PDPA) ของราชอาณาจักรไทย
2. ข้อมูลที่เราเก็บรวบรวม (Data We Collect)
2.1 ข้อมูลที่ท่านให้โดยตรง
• ชื่อ-นามสกุล และข้อมูลติดต่อ (อีเมล, เบอร์โทรศัพท์)
• ข้อมูลการจอง: วันที่, เวลา, ประเภทบริการที่เลือก
• ข้อมูลการชำระเงิน (ผ่าน Omise Payment Gateway — เราไม่เก็บข้อมูลบัตรเครดิต/เดบิตโดยตรง)
• ความต้องการพิเศษ หรือหมายเหตุเพิ่มเติมที่ท่านระบุ
2.2 ข้อมูลที่เก็บโดยอัตโนมัติ
• ข้อมูลการใช้งานเว็บไซต์ (IP Address, เบราว์เซอร์, หน้าที่เยี่ยมชม)
• คุกกี้และเทคโนโลยีที่คล้ายกัน
2.3 ข้อมูลภาพถ่าย
• ภาพถ่ายที่ถ่ายระหว่างบริการของสตูดิโอ จะถูกเก็บเพื่อมอบแก่ลูกค้าและใช้ในการสื่อสารของสตูดิโอตามที่ได้รับความยินยอม
3. วัตถุประสงค์ในการใช้ข้อมูล (Purpose of Use)
• ดำเนินการจองและยืนยันการนัดหมาย
• ประมวลผลการชำระเงินผ่านระบบ Omise
• ส่งการแจ้งเตือนและยืนยันการจองทางอีเมลหรือ SMS
• ปรับปรุงคุณภาพบริการและประสบการณ์ของลูกค้า
• ส่งข้อมูลโปรโมชั่นและข่าวสาร (เฉพาะเมื่อได้รับความยินยอม)
• ปฏิบัติตามกฎหมายและระเบียบข้อบังคับที่เกี่ยวข้อง
4. การประมวลผลการชำระเงิน (Payment Processing — Omise)
สตูดิโอใช้บริการ Omise (Omise Co., Ltd.) ซึ่งเป็น Payment Gateway ที่ได้รับมาตรฐาน PCI-DSS สำหรับการประมวลผลการชำระเงินออนไลน์ทั้งหมด
• ข้อมูลบัตรเครดิต/เดบิต จะถูกรับและเก็บโดย Omise โดยตรง — Achiraya Studio ไม่มีการจัดเก็บข้อมูลบัตรไว้บนเซิร์ฟเวอร์ของตนเอง
• การใช้ข้อมูลของ Omise อยู่ภายใต้นโยบายความเป็นส่วนตัวของ Omise (www.omise.co/privacy)
• สตูดิโอจัดเก็บเฉพาะ: หมายเลขอ้างอิงการทำรายการ, จำนวนเงิน, วันที่ และสถานะการชำระเงิน
5. การเปิดเผยข้อมูลแก่บุคคลที่สาม (Disclosure to Third Parties)
เราจะไม่ขาย ให้เช่า หรือเปิดเผยข้อมูลส่วนบุคคลของท่านแก่บุคคลภายนอก ยกเว้นในกรณีดังต่อไปนี้:
• ผู้ให้บริการที่จำเป็น: Omise (การชำระเงิน), ผู้ให้บริการโฮสติ้ง, ระบบส่งอีเมล — ภายใต้ข้อตกลงที่ผูกพันทางกฎหมาย
• หน่วยงานราชการ: เมื่อมีคำสั่งตามกฎหมายหรือกระบวนการทางกฎหมาย
• กรณีฉุกเฉิน: เพื่อปกป้องความปลอดภัยหรือสิทธิ์ของท่านหรือบุคคลอื่น
6. ระยะเวลาการเก็บรักษาข้อมูล (Retention Period)
• ข้อมูลการจองและการชำระเงิน: 5 ปี นับจากวันที่ทำรายการ (ตามข้อกำหนดทางภาษีและบัญชี)
• ข้อมูลการตลาด: จนกว่าท่านจะขอยกเลิกการรับข่าวสาร
• ข้อมูลบัญชีผู้ใช้: ตลอดอายุบัญชี หรือ 2 ปีหลังจากไม่มีกิจกรรม
• ข้อมูล Log และการใช้เว็บไซต์: ไม่เกิน 1 ปี
7. สิทธิ์ของเจ้าของข้อมูล (Your Rights under PDPA)
ตามพระราชบัญญัติคุ้มครองข้อมูลส่วนบุคคล พ.ศ. 2562 ท่านมีสิทธิ์ดังต่อไปนี้:
• สิทธิ์ในการเข้าถึงข้อมูล (Right of Access) — ขอสำเนาข้อมูลของท่าน
• สิทธิ์ในการแก้ไขข้อมูล (Right of Rectification) — ขอแก้ไขข้อมูลที่ไม่ถูกต้อง
• สิทธิ์ในการลบข้อมูล (Right of Erasure) — ขอลบข้อมูลเมื่อไม่จำเป็น
• สิทธิ์ในการคัดค้าน (Right to Object) — คัดค้านการประมวลผลข้อมูลเพื่อการตลาด
• สิทธิ์ในการถอนความยินยอม (Right to Withdraw Consent) — ถอนความยินยอมได้ทุกเวลา
• สิทธิ์ในการโอนย้ายข้อมูล (Right to Data Portability)

ท่านสามารถใช้สิทธิ์โดยติดต่อเราตามช่องทางที่ระบุในข้อ 10 เราจะดำเนินการภายใน 30 วัน
8. มาตรการความปลอดภัย (Security Measures)
• การเข้ารหัสข้อมูลด้วย TLS/SSL สำหรับการรับส่งข้อมูลทั้งหมด
• Omise ผ่านการรับรองมาตรฐาน PCI-DSS Level 1 สำหรับข้อมูลการชำระเงิน
• การจำกัดสิทธิ์การเข้าถึงข้อมูลตามความจำเป็น (Principle of Least Privilege)
• การสำรองข้อมูลอย่างสม่ำเสมอและเป็นความลับ
9. คุกกี้ (Cookies)
เว็บไซต์ jackachihouse.com ใช้คุกกี้เพื่อ:
• คุกกี้ที่จำเป็น (Essential Cookies): การทำงานของระบบจองและตะกร้าสินค้า
• คุกกี้วิเคราะห์ (Analytics Cookies): ทำความเข้าใจพฤติกรรมการใช้งาน (เช่น Google Analytics)
• คุกกี้การตลาด (Marketing Cookies): การแสดงโฆษณาที่เกี่ยวข้อง (เฉพาะเมื่อได้รับความยินยอม)

ท่านสามารถปรับการตั้งค่าคุกกี้ได้ผ่านแบนเนอร์ความยินยอมบนเว็บไซต์ หรือผ่านการตั้งค่าเบราว์เซอร์
10. ติดต่อเรา (Contact Us)
หากท่านมีคำถามเกี่ยวกับนโยบายนี้หรือต้องการใช้สิทธิ์ของท่าน กรุณาติดต่อ:

Achiraya Studio
• เว็บไซต์: www.jackachihouse.com
• อีเมล: support@jackachihouse.com
• โทรศัพท์: (+66) 83 013 1218 หรือ (+66) 92 498 8850
• ที่อยู่: 36 ถ.อรุณอมรินทร์ แขวงวัดกัลยา เขตธนบุรี กรุงเทพมหานคร 10600
11. การแก้ไขนโยบาย (Policy Updates)
สตูดิโออาจแก้ไขนโยบายนี้เป็นครั้งคราว การเปลี่ยนแปลงที่มีนัยสำคัญจะแจ้งให้ทราบผ่านเว็บไซต์หรืออีเมล วันที่มีผลบังคับใช้ล่าสุดจะแสดงที่ด้านบนของนโยบายนี้เสมอ


─── สิ้นสุดนโยบายความเป็นส่วนตัว ───`,
    },
    zh: {
      title: '隐私政策',
      rawText: `隐私政策
Achiraya Studio  |  www.jackachihouse.com
生效日期：2025年6月1日

1. 简介
Achiraya Studio（"工作室"、"我们"、"我们的"）在泰国曼谷郑王庙（Wat Arun Ratchawararam，黎明寺）提供专业化妆服务、正宗泰式服装租赁与穿戴以及摄影服务。我们致力于保护客户及网站访问者的个人数据。
本隐私政策说明了我们收集哪些个人数据、如何使用这些数据、可能向谁披露这些数据，以及您对这些数据享有的权利。我们依据泰王国《个人数据保护法》（佛历2562年，2019年）（PDPA）处理个人数据。
2. 我们收集的数据
2.1 您直接提供的数据
• 全名及联系方式（电子邮件地址、电话号码）
• 预订信息：首选日期、时间及所选服务类型
• 支付信息 — 通过 Omise 支付网关处理（我们不直接存储您的银行卡数据）
• 您在预订时提交的特别要求或其他备注
2.2 自动收集的数据
• 网站使用数据（IP 地址、浏览器类型、访问页面、会话时长）
• Cookie 及类似的跟踪技术
2.3 摄影数据
拍摄期间拍摄的照片将交付给您，并可能在事先征得您同意的情况下用于工作室的宣传和营销材料。
3. 使用目的
我们将您的个人数据用于以下目的：
• 处理预订并确认预约
• 通过 Omise 支付平台安全地处理付款
• 通过电子邮件或短信发送预订确认、提醒和服务更新
• 提升服务质量和客户体验
• 发送促销优惠和新闻通讯（仅在您明确同意的情况下）
• 遵守适用的法律法规
4. 支付处理 — Omise
工作室使用 Omise（Omise Co., Ltd.，一家通过 PCI-DSS 认证的支付网关）处理所有在线支付交易。
信用卡和借记卡数据由 Omise 直接收集和存储 — Achiraya Studio 不会在其自有服务器上存储银行卡信息。
Omise 对您支付数据的处理受 Omise 隐私政策约束，详情请访问 www.omise.co/privacy。
工作室仅保留：交易参考编号、收费金额、交易日期及支付状态。
5. 向第三方披露
我们不会出售、出租或与第三方共享您的个人数据，但以下情况除外：
• 服务提供商：Omise（支付）、托管服务商及电子邮件发送服务 — 均受合同数据保护义务的约束
• 政府或监管机构：根据法律、法院命令或法律程序要求时
• 紧急情况：为保护您或他人的人身安全或权利
6. 数据保留
• 预订及支付记录：自交易之日起 5 年（依据泰国税务及会计要求）
• 营销数据：直至您撤回同意或取消订阅
• 用户账户数据：账户存续期间，或最后一次活动后 2 年
• 网站日志及分析数据：不超过 1 年
7. 您在 PDPA 下的权利
根据《个人数据保护法》佛历2562年，您享有以下权利：
• 查阅权 — 要求我们提供所持有的关于您的个人数据副本
• 更正权 — 要求更正不准确或不完整的数据
• 删除权 — 在数据不再必要时要求删除您的数据
• 反对权 — 反对将您的数据用于直接营销目的
• 撤回同意权 — 随时撤回同意，且不影响此前基于同意进行的处理
• 数据可携带权 — 以结构化、通用格式接收您的数据

如需行使上述任何权利，请使用第10节中的联系方式与我们联系。我们将在收到请求后 30 天内予以回复。
8. 安全措施
• 我们网站的所有数据传输均采用 TLS/SSL 加密
• 支付卡数据受 Omise PCI-DSS 一级认证保护
• 对个人数据的访问遵循"最小权限原则"，仅限有必要知悉的人员访问
• 定期进行数据备份并安全存储
9. Cookie
网站 jackachihouse.com 使用 Cookie，用途如下：
• 必要 Cookie：预订系统及网站核心功能所必需
• 分析 Cookie：了解访客如何与网站互动（例如 Google Analytics）
• 营销 Cookie：投放相关广告（仅在您同意的情况下）

您可以通过网站上显示的同意横幅管理 Cookie 偏好，或调整浏览器设置。
10. 联系我们
如有关于本隐私政策的问题，或希望行使您的数据权利，请联系：

Achiraya Studio
网站：www.jackachihouse.com
电子邮件：support@jackachihouse.com
电话：(+66) 83 013 1218  或  (+66) 92 498 8850
地址：36 Arun Amarin Road, Wat Kalaya Subdistrict, Thonburi District, Bangkok 10600, Thailand
11. 政策更新
我们可能会不时更新本隐私政策。任何重大变更将通过网站或电子邮件通知。本文件顶部的生效日期始终反映最新版本。

─── 隐私政策结束 ───`,
    },
  },
  termsOfUse: {
    en: {
      title: 'Terms of Use',
      rawText: `Terms of Use
Achiraya Studio  |  www.jackachihouse.com
Effective Date: 1 June 2025
1. Acceptance of Terms
By accessing the website jackachihouse.com, making a booking, or completing a deposit payment through the Achiraya Studio online system, you confirm that you have read, understood, and agreed to these Terms of Use in their entirety. If you do not agree to these terms, please refrain from using the service.
2. About the Service
Achiraya Studio provides the following services at Wat Arun Ratchawararam (Temple of Dawn), Bangkok:
• Professional makeup services
• Authentic Thai costume rental and dressing
• Professional photography within the temple grounds

This web application serves solely as an online booking and deposit payment platform. The actual services are rendered at the Studio's physical location on your confirmed appointment date and time.
3. Booking and Deposit Payment
3.1 Booking Process
• Bookings are made via jackachihouse.com by selecting your preferred date, time, and service type
• A booking is confirmed only upon receipt of a confirmation email or SMS from the Studio
• The Studio reserves the right to decline or cancel a booking if the service cannot be accommodated
3.2 Deposit
• This web application collects a deposit only, for the purpose of securing your reservation
• The remaining balance is payable in person at the Studio on the day of your appointment
• All deposit payments are processed through Omise Payment Gateway, which holds PCI-DSS certification
4. Cancellation and Refund Policy
⚠   NO REFUNDS UNDER ANY CIRCUMSTANCES   ⚠
All deposit payments made through this platform are strictly non-refundable.

Full policy details:
• This web application collects a deposit solely to confirm your booking — it does not process full-service payments
• Deposits are non-refundable in all cases, including but not limited to: customer cancellation, rescheduling requests, adverse weather, force majeure events, or any personal circumstances
• In the event that the Studio cancels or is unable to fulfill the appointment, the Studio will arrange a replacement appointment at no additional charge
• Booking rights are non-transferable to another person

By completing the deposit payment, you acknowledge and accept this no-refund policy.
5. Acceptable Use
By using this website, you agree to:
• Provide accurate and truthful information when making a booking
• Not use the platform for any unlawful or harmful purpose
• Not attempt to gain unauthorised access to any part of the system
• Not engage in any conduct that may disrupt or interfere with the Studio's services or website
6. Intellectual Property
All content on jackachihouse.com — including text, images, logos, design elements, and software — is the property of Achiraya Studio and is protected under the intellectual property laws of the Kingdom of Thailand.
• No content may be reproduced, copied, adapted, or distributed without prior written permission from the Studio
• Photographs taken during your session are jointly owned by the Studio and the customer; the Studio may use such images in promotional materials subject to your prior consent
7. Limitation of Liability
• The Studio will deliver all services to the highest professional standard; however, it accepts no liability for loss or damage arising from circumstances beyond its reasonable control
• In the event of damage to a customer's personal property during a session, the Studio's liability is limited to no more than the value of the service fee paid
• The Studio is not responsible for the loss or theft of valuables brought onto the premises by the customer
8. Health and Safety
• Please inform your makeup artist in advance of any cosmetic allergies, sensitive skin conditions, or skin disorders
• The Studio uses internationally certified cosmetic products; the Studio accepts no liability for allergic reactions arising from conditions not disclosed in advance
• Customers must observe the rules and customs of Wat Arun Ratchawararam while on temple grounds during their photography session
9. Amendments to These Terms
The Studio reserves the right to amend these Terms of Use at any time. Changes take effect immediately upon publication on the website. Continued use of the service after any amendment constitutes acceptance of the revised terms.
10. Governing Law
These Terms of Use are governed by and construed in accordance with the laws of the Kingdom of Thailand. Any dispute arising in connection with these terms shall fall within the jurisdiction of the competent Thai courts.
11. Contact Us
If you have any questions or concerns about these Terms of Use, please contact:

Achiraya Studio
• Website: www.jackachihouse.com
• Email: support@jackachihouse.com
• Phone: (+66) 83 013 1218  or  (+66) 92 498 8850
• Address: 36 Arun Amarin Road, Wat Kalaya Subdistrict, Thonburi District, Bangkok 10600, Thailand


─── End of Terms of Use ───`,
    },
    th: {
      title: 'ข้อกำหนดการใช้บริการ',
      rawText: `ข้อกำหนดการใช้บริการ
Achiraya Studio  |  www.jackachihouse.com
มีผลบังคับใช้: 1 มิถุนายน 2568  (Effective: 1 June 2025)
1. การยอมรับข้อกำหนด
การเข้าใช้งานเว็บไซต์ jackachihouse.com การจองบริการ หรือการชำระเงินมัดจำผ่านระบบออนไลน์ของ Achiraya Studio ถือว่าท่านได้อ่าน เข้าใจ และยอมรับข้อกำหนดการใช้บริการฉบับนี้ทุกประการ หากท่านไม่ยอมรับข้อกำหนดดังกล่าว กรุณางดใช้บริการ
2. เกี่ยวกับบริการ
Achiraya Studio ให้บริการ ณ วัดอรุณราชวราราม กรุงเทพมหานคร ประกอบด้วย:
• บริการแต่งหน้ามืออาชีพ
• บริการเช่าและสวมชุดไทยแท้
• บริการถ่ายภาพสวยงามในบริเวณวัดอรุณราชวราราม

ระบบเว็บแอปพลิเคชันนี้ทำหน้าที่รับการจองและชำระเงินมัดจำออนไลน์เท่านั้น บริการจริงจะดำเนินการ ณ สถานที่ให้บริการตามวันและเวลาที่นัดหมาย
3. การจองและการชำระเงินมัดจำ
3.1 ขั้นตอนการจอง
• ท่านสามารถจองบริการผ่านเว็บไซต์ jackachihouse.com โดยเลือกวันที่ เวลา และประเภทบริการ
• การจองจะสมบูรณ์เมื่อได้รับการยืนยันจากสตูดิโอทางอีเมลหรือ SMS
• สตูดิโอขอสงวนสิทธิ์ในการปฏิเสธหรือยกเลิกการจองในกรณีที่ไม่สามารถให้บริการได้
3.2 เงินมัดจำ
• ระบบเว็บแอปพลิเคชันนี้รับชำระเฉพาะเงินมัดจำเพื่อยืนยันการจองเท่านั้น
• ยอดคงเหลือสำหรับบริการจะชำระ ณ สถานที่ในวันที่รับบริการ
• การชำระเงินมัดจำดำเนินการผ่าน Omise Payment Gateway ซึ่งได้รับมาตรฐาน PCI-DSS
4. นโยบายการคืนเงินและการยกเลิก
⚠  นโยบายสำคัญ: ไม่มีการคืนเงินในทุกกรณี  ⚠
เงินมัดจำที่ชำระผ่านระบบออนไลน์ไม่สามารถขอคืนได้ไม่ว่าด้วยเหตุใดทั้งสิ้น

รายละเอียดนโยบาย:
• ระบบเว็บแอปพลิเคชันนี้ทำหน้าที่รับชำระเงินมัดจำเพื่อยืนยันการจองเท่านั้น
• เงินมัดจำที่ชำระแล้วจะไม่ถูกคืนในทุกกรณี รวมถึงแต่ไม่จำกัดเฉพาะ: การยกเลิกโดยลูกค้า, การเปลี่ยนแปลงกำหนดการ, สภาพอากาศ, เหตุสุดวิสัย หรือเหตุผลส่วนตัวใดๆ
• กรณีที่สตูดิโอเป็นผู้ยกเลิกหรือไม่สามารถให้บริการได้ตามที่นัดหมาย สตูดิโอจะดำเนินการนัดหมายวันใหม่ให้กับท่านโดยไม่มีค่าใช้จ่ายเพิ่มเติม
• การโอนสิทธิ์การจองให้ผู้อื่นไม่สามารถกระทำได้

การทำรายการชำระเงินมัดจำถือว่าท่านได้รับทราบและยอมรับนโยบายการไม่คืนเงินนี้แล้ว
5. การใช้งานเว็บไซต์
ท่านตกลงที่จะ:
• ให้ข้อมูลที่ถูกต้องและเป็นความจริงในการจอง
• ไม่ใช้ระบบเพื่อวัตถุประสงค์ที่ผิดกฎหมายหรือเป็นอันตราย
• ไม่พยายามเข้าถึงระบบโดยไม่ได้รับอนุญาต
• ไม่กระทำการใดๆ ที่อาจรบกวนหรือขัดขวางการให้บริการของสตูดิโอ
6. ทรัพย์สินทางปัญญา
เนื้อหาทั้งหมดบนเว็บไซต์ jackachihouse.com รวมถึงข้อความ รูปภาพ โลโก้ การออกแบบ และซอฟต์แวร์ เป็นทรัพย์สินของ Achiraya Studio และได้รับการคุ้มครองโดยกฎหมายทรัพย์สินทางปัญญาของราชอาณาจักรไทย
• ห้ามทำซ้ำ คัดลอก ดัดแปลง หรือเผยแพร่เนื้อหาใดๆ โดยไม่ได้รับอนุญาตเป็นลายลักษณ์อักษร
• ภาพถ่ายที่ถ่ายระหว่างการให้บริการเป็นกรรมสิทธิ์ร่วมระหว่างสตูดิโอและลูกค้า สตูดิโออาจนำไปใช้ในสื่อโฆษณาตามที่ได้รับความยินยอม
7. ข้อจำกัดความรับผิด
• สตูดิโอจะดำเนินการให้บริการด้วยมาตรฐานวิชาชีพสูงสุด อย่างไรก็ตาม สตูดิโอไม่รับผิดชอบต่อความเสียหายที่เกิดจากเหตุสุดวิสัยหรือสถานการณ์นอกเหนือการควบคุม
• ในกรณีที่เกิดความเสียหายต่อทรัพย์สินส่วนตัวของลูกค้าระหว่างรับบริการ ความรับผิดของสตูดิโอจำกัดไม่เกินมูลค่าของบริการที่ชำระ
• สตูดิโอไม่รับผิดชอบต่อการสูญหายหรือเสียหายของสิ่งของมีค่าที่ลูกค้านำมา
8. สุขภาพและความปลอดภัย
• กรุณาแจ้งช่างแต่งหน้าล่วงหน้าหากท่านมีอาการแพ้เครื่องสำอาง ผิวแพ้ง่าย หรือโรคผิวหนัง
• สตูดิโอใช้ผลิตภัณฑ์เครื่องสำอางมาตรฐานสากล หากเกิดอาการแพ้ที่ไม่ได้แจ้งล่วงหน้า สตูดิโอไม่รับผิดชอบ
• ลูกค้าต้องปฏิบัติตามกฎระเบียบของวัดอรุณราชวรารามในขณะถ่ายภาพในบริเวณวัด
9. การแก้ไขข้อกำหนด
สตูดิโอขอสงวนสิทธิ์ในการแก้ไขข้อกำหนดการใช้บริการนี้ได้ตลอดเวลา การเปลี่ยนแปลงจะมีผลบังคับใช้ทันทีที่เผยแพร่บนเว็บไซต์ การใช้บริการต่อเนื่องหลังจากการเปลี่ยนแปลงถือว่าท่านยอมรับข้อกำหนดใหม่
10. กฎหมายที่ใช้บังคับ
ข้อกำหนดการใช้บริการฉบับนี้อยู่ภายใต้และตีความตามกฎหมายแห่งราชอาณาจักรไทย ข้อพิพาทใดๆ ที่เกิดขึ้นจะอยู่ในเขตอำนาจของศาลไทยที่มีอำนาจพิจารณา
11. ติดต่อเรา
หากท่านมีคำถามหรือข้อสงสัยเกี่ยวกับข้อกำหนดการใช้บริการนี้ กรุณาติดต่อ:

Achiraya Studio
• เว็บไซต์: www.jackachihouse.com
• อีเมล: support@jackachihouse.com
• โทรศัพท์: (+66) 83 013 1218 หรือ (+66) 92 498 8850
• ที่อยู่: 36 ถ.อรุณอมรินทร์ แขวงวัดกัลยา เขตธนบุรี กรุงเทพมหานคร 10600


─── สิ้นสุดข้อกำหนดการใช้บริการ ───`,
    },
    zh: {
      title: '使用条款',
      rawText: `使用条款
Achiraya Studio  |  www.jackachihouse.com
生效日期：2025年6月1日

1. 条款接受
通过访问网站 jackachihouse.com、进行预订或通过 Achiraya Studio 在线系统完成定金支付，即表示您确认已完整阅读、理解并同意本使用条款。如果您不同意这些条款，请勿使用本服务。
2. 关于服务
Achiraya Studio 在泰国曼谷郑王庙（Wat Arun Ratchawararam，黎明寺）提供以下服务：
• 专业化妆服务
• 正宗泰式服装租赁与穿戴
• 寺庙区域内的专业摄影服务

本网站应用仅作为在线预订及定金支付平台。实际服务将在您确认的预约日期和时间，于工作室实体地点提供。
3. 预订及定金支付
3.1 预订流程
• 通过 jackachihouse.com 选择您偏好的日期、时间及服务类型进行预订
• 仅在收到工作室发送的确认电子邮件或短信后，预订方为确认
• 如无法安排服务，工作室保留拒绝或取消预订的权利
3.2 定金
• 本网站应用仅收取定金，用于确保您的预约
• 剩余款项请于预约当日在工作室现场支付
• 所有定金支付均通过持有 PCI-DSS 认证的 Omise 支付网关处理
4. 取消及退款政策
⚠   任何情况下均不予退款   ⚠
通过本平台支付的所有定金均严格不予退款。

完整政策详情：
• 本网站应用收取定金仅用于确认您的预订 — 不处理全额服务付款
• 定金在任何情况下均不予退款，包括但不限于：客户取消、改期请求、恶劣天气、不可抗力事件或任何个人原因
• 若工作室取消或无法履行预约，工作室将免费为您安排替代预约
• 预订权利不可转让给他人

完成定金支付即表示您知悉并接受本不予退款政策。
5. 可接受的使用方式
使用本网站即表示您同意：
• 预订时提供准确真实的信息
• 不将本平台用于任何非法或有害目的
• 不试图未经授权访问系统的任何部分
• 不进行任何可能干扰或破坏工作室服务或网站的行为
6. 知识产权
jackachihouse.com 上的所有内容 — 包括文字、图片、标识、设计元素及软件 — 均为 Achiraya Studio 的财产，并受泰王国知识产权法律保护。
• 未经工作室事先书面许可，不得复制、拷贝、改编或分发任何内容
• 拍摄期间拍摄的照片由工作室与客户共同拥有；工作室可在事先征得您同意的情况下将此类图像用于宣传材料
7. 责任限制
• 工作室将以最高的专业标准提供所有服务；但对于超出其合理控制范围的情况所造成的损失或损害，工作室不承担责任
• 若在服务期间客户的个人财产发生损坏，工作室的责任上限不超过已支付的服务费用金额
• 工作室对客户带入场所的贵重物品丢失或被盗不承担责任
8. 健康与安全
• 请提前告知化妆师您是否有化妆品过敏、敏感皮肤状况或皮肤疾病
• 工作室使用国际认证化妆品；对于因未提前告知的病情而引起的过敏反应，工作室不承担责任
• 客户在摄影期间于郑王庙区域内，须遵守寺庙的规则和习俗
9. 条款修订
工作室保留随时修订本使用条款的权利。变更在网站发布后立即生效。在任何修订后继续使用本服务即表示接受修订后的条款。
10. 适用法律
本使用条款受泰王国法律管辖并依其解释。因这些条款引起的任何争议应属泰王国主管法院管辖。
11. 联系我们
如您对本使用条款有任何疑问或顾虑，请联系：

Achiraya Studio
网站：www.jackachihouse.com
电子邮件：support@jackachihouse.com
电话：(+66) 83 013 1218  或  (+66) 92 498 8850
地址：36 Arun Amarin Road, Wat Kalaya Subdistrict, Thonburi District, Bangkok 10600, Thailand

─── 使用条款结束 ───`,
    },
  },
}
